import { IStairConfig, IStepGeometry } from '@stair-craft/common/util';

import { IStairNorms, RESIDENTIAL_NORMS } from '../../norms';
import { ONE_SIDED_WARNING_BAND_FRACTION } from '../../validator';
import { calculateHeadroom } from '../shared-calculate-headroom/calculate-headroom';
import { NORM_EDGE_BAND_FRACTION, outOfNormShare } from '../shared-pick-in-norm-flight2-count/pick-in-norm-flight2-count';
import { stairPlacement } from '../shared-stair-placement/stair-placement';
import { ISpiralLayout, calculateLayout } from '../spiral-calculate-layout/calculate-layout';
import { EXIT_PLATFORM_ANGLE_DEG, generateExitSteps } from '../spiral-generate-exit-steps/generate-exit-steps';
import { generateSteps } from '../spiral-generate-steps/generate-steps';

/** One spiral the auto layout may build: a riser count and a whole-degree rotation. */
interface ISpiralCandidate {
    readonly stepCount: number;
    readonly rotationDeg: number;
    /** Out-of-norm share of riser, tread, Blondel, angle, inner depth and the estimated headroom. */
    readonly penalty: number;
    /** Distance from the ideal Blondel and the ideal riser (mm); breaks ties between in-norm candidates. */
    readonly preference: number;
}

/** Full calculations of headroom the pick may run before it settles for the best one seen. */
const MAX_HEADROOM_CHECKS: number = 6;

const SCORE_EPSILON: number = 1e-9;

const DEG: number = Math.PI / 180;

/** How far the headroom sits below the "ok" part of its one-sided limit, as a share of the limit. */
function headroomShare(headroom: number, norms: IStairNorms): number {
    const okFrom: number = norms.headroomMin * (1 + ONE_SIDED_WARNING_BAND_FRACTION);
    return Math.max(0, okFrom - headroom) / norms.headroomMin;
}

/** The riser counts worth trying: the pinned one, or every count whose riser is "ok". */
function stepCountCandidates(config: IStairConfig, norms: IStairNorms, totalRise: number): number[] {
    if (config.stepCount != null || config.riserHeight != null) {
        return [calculateLayout(config, norms).stepCount];
    }
    const band: number = (norms.riserHeightMax - norms.riserHeightMin) * NORM_EDGE_BAND_FRACTION;
    const lo: number = Math.max(3, Math.ceil(totalRise / (norms.riserHeightMax - band)));
    const hi: number = Math.floor(totalRise / (norms.riserHeightMin + band));
    const counts: number[] = [];
    for (let count: number = lo; count <= hi; count++) {
        counts.push(count);
    }
    return counts.length > 0 ? counts : [calculateLayout(config, norms).stepCount];
}

/** The step angle (rad) whose chord at the walking line is `tread`; capped at a half turn. */
function stepAngleForTread(tread: number, walkingLineR: number): number {
    return 2 * Math.asin(Math.min(1, tread / (2 * walkingLineR)));
}

/**
 * The headroom under the exit platform, estimated without building the stair: the platform spans
 * a quarter turn past the last tread and hangs over the treads of the angle it overlaps. The
 * nosing line is taken half a riser above its height at the platform's edge: measured there, the
 * estimate ran up to 90 mm (about half a riser) above what `calculateHeadroom` finds, because the
 * nosing line rises along the walking direction, not by angle, and the pick settled on a warning.
 */
function estimateHeadroom(layout: ISpiralLayout, config: IStairConfig): number {
    const totalRise: number = config.floorHeight + config.floorThickness;
    const overhangDeg: number = layout.totalRotationDeg + EXIT_PLATFORM_ANGLE_DEG - 360;
    if (overhangDeg <= 0) {
        return totalRise;
    }
    const stepDeg: number = layout.anglePerStepRad / DEG;
    const nosingY: number = (overhangDeg / stepDeg + 1.5) * layout.riserHeight;
    return totalRise - config.treadThickness - nosingY;
}

/** Score one candidate from its layout alone — every check but the real headroom. */
function scoreCandidate(layout: ISpiralLayout, config: IStairConfig, norms: IStairNorms): ISpiralCandidate {
    const { riserHeight, treadDepthAtWalkingLine: tread, anglePerStepRad } = layout;
    const blondel: number = 2 * riserHeight + tread;
    const angle: number = Math.atan2(riserHeight, tread) / DEG;
    const innerDepth: number = 2 * (layout.innerRadius + norms.winderMeasureOffset) * Math.sin(anglePerStepRad / 2);
    const penalty: number =
        outOfNormShare(riserHeight, norms.riserHeightMin, norms.riserHeightMax) +
        outOfNormShare(tread, norms.treadDepthMin, norms.treadDepthMax) +
        outOfNormShare(blondel, norms.blondelMin, norms.blondelMax) +
        outOfNormShare(angle, norms.angleMin, norms.angleMax) +
        Math.max(0, norms.winderMinInnerDepth - innerDepth) / norms.winderMinInnerDepth +
        headroomShare(estimateHeadroom(layout, config), norms);
    return {
        penalty,
        stepCount: layout.stepCount,
        rotationDeg: layout.totalRotationDeg,
        preference: Math.abs(blondel - norms.blondelIdeal) + Math.abs(riserHeight - norms.riserHeightIdeal),
    };
}

/** Every whole-degree rotation whose walking-line tread lands in the "ok" tread band, for each riser count. */
function candidates(config: IStairConfig, norms: IStairNorms): ISpiralCandidate[] {
    const totalRise: number = config.floorHeight + config.floorThickness;
    const probe: ISpiralLayout = calculateLayout({ ...config, spiralRotation: 360 }, norms);
    const band: number = (norms.treadDepthMax - norms.treadDepthMin) * NORM_EDGE_BAND_FRACTION;
    const minStep: number = stepAngleForTread(norms.treadDepthMin + band, probe.walkingLineR) / DEG;
    const maxStep: number = stepAngleForTread(norms.treadDepthMax - band, probe.walkingLineR) / DEG;
    const all: ISpiralCandidate[] = [];
    for (const stepCount of stepCountCandidates(config, norms, totalRise)) {
        const treads: number = Math.max(1, stepCount - 1);
        const lo: number = Math.ceil(treads * minStep);
        const hi: number = Math.max(lo, Math.floor(treads * maxStep));
        for (let rotationDeg: number = lo; rotationDeg <= hi; rotationDeg++) {
            all.push(scoreCandidate(calculateLayout({ ...config, stepCount, spiralRotation: rotationDeg }, norms), config, norms));
        }
    }
    return all.sort(
        (a: ISpiralCandidate, b: ISpiralCandidate) =>
            (Math.abs(a.penalty - b.penalty) > SCORE_EPSILON ? a.penalty - b.penalty : 0) || a.preference - b.preference
    );
}

/** The candidate's layout and its penalty with the real headroom in place of the estimate. */
function checkHeadroom(candidate: ISpiralCandidate, config: IStairConfig, norms: IStairNorms): { layout: ISpiralLayout; penalty: number } {
    const layout: ISpiralLayout = calculateLayout(
        { ...config, stepCount: candidate.stepCount, spiralRotation: candidate.rotationDeg },
        norms
    );
    const steps: IStepGeometry[] = [...generateSteps(layout, config), ...generateExitSteps(layout, config)];
    const headroom: number = calculateHeadroom(steps, config, stairPlacement(config, config, layout.outerRadius * 2), false).headroom;
    const estimated: number = headroomShare(estimateHeadroom(layout, config), norms);
    return { layout, penalty: candidate.penalty - estimated + headroomShare(headroom, norms) };
}

/**
 * The spiral layout. A rotation the config names is built as is. With none, the layout is picked
 * the way a spiral stair maker does it: the riser count follows the floor height, the step angle
 * keeps the walking-line tread and the Blondel value in norm and the inner depth above its minimum,
 * and the rotation follows from the two. The exit platform hangs a quarter turn past the last
 * tread, so a rotation near a full turn puts it over the first steps; every candidate is scored by
 * how far it sits outside the validator's "ok" bands, headroom included, and ties go to the ideal
 * Blondel value and riser. A pinned riser count or riser keeps its count, and only the angle moves.
 */
export function pickAutoLayout(config: IStairConfig, norms: IStairNorms): ISpiralLayout {
    if (config.spiralRotation != null) {
        return calculateLayout(config, norms);
    }
    const ordered: ISpiralCandidate[] = candidates(config, norms);
    let best: { layout: ISpiralLayout; penalty: number } | null = null;
    for (const candidate of ordered.slice(0, MAX_HEADROOM_CHECKS)) {
        const checked: { layout: ISpiralLayout; penalty: number } = checkHeadroom(candidate, config, norms);
        if (checked.penalty <= SCORE_EPSILON) {
            return checked.layout;
        }
        if (best === null || checked.penalty < best.penalty - SCORE_EPSILON) {
            best = checked;
        }
    }
    return best?.layout ?? calculateLayout({ ...config, spiralRotation: 360 }, norms);
}

/**
 * The rotation (degrees) the spiral is built with: the config's own, or the auto layout's pick.
 * The plan's rotation dial and step detail read it here, so they draw the stair the calculator built.
 */
export function spiralRotationDeg(config: IStairConfig, norms: IStairNorms = RESIDENTIAL_NORMS): number {
    return pickAutoLayout(config, norms).totalRotationDeg;
}
